/**
 * Why the flow stopped, in terms the applicant can act on.
 *
 * Shared by the two steps that require a position (consent and signing) so the
 * five messages exist once. Duplicating them would guarantee they drift, and
 * the drift would be invisible: both screens would still render *a* sentence.
 *
 * Five reasons, five messages, and only one of them is "check your browser
 * settings". A single generic message would send the person whose GPS simply
 * has no signal indoors into a settings menu where everything is already
 * correct, and would tell the person on a misconfigured HTTP deployment that
 * the problem is on their end.
 */
import { useTranslation } from 'react-i18next';

import type { PositionDenial } from './geolocation';

export function LocationBlocked({ reason }: { reason: PositionDenial }) {
  const { t } = useTranslation();

  const body: Record<PositionDenial, string> = {
    permission_denied: t('flow.location.permission_denied', {
      defaultValue:
        'Location access is blocked for this site. Open your browser settings for this page, allow location, then reload.',
    }),
    position_unavailable: t('flow.location.position_unavailable', {
      defaultValue:
        'Your device could not determine a location. Move near a window or go outside, then try again.',
    }),
    timeout: t('flow.location.timeout', {
      defaultValue: 'Finding your location took too long. Please try again.',
    }),
    unsupported: t('flow.location.unsupported', {
      defaultValue:
        'This browser cannot share a location. Please continue on a different browser or on your phone.',
    }),
    insecure_context: t('flow.location.insecure_context', {
      defaultValue:
        'This page is not on a secure connection, so location cannot be shared. Please contact support — this is not something you can fix.',
    }),
  };

  return (
    <p className="step-text" role="alert" data-testid="location-blocked" data-reason={reason}>
      {body[reason]}
    </p>
  );
}
